"use client";

import { QuoteGreetings } from "@/components/quotes/quotes-template/quote-greetings";
import { QuoteHeader } from "@/components/quotes/quotes-template/quote-header";
import { getContactName } from "@/components/customers/helpers/get-contact-name";

import { HolidayQuoteBrandProvider } from "./contexts/holiday-quote-brand-provider";
import { QuoteFooter } from "./quote-footer";
import { iHolidayQuoteX } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { iContact } from "@/@sdk/crm/crm.models";
import {
  iOrganization,
  iUser,
} from "@/@sdk/user-management/user-management.models";
import { useMemo } from "react";
import { iAgentSectionData } from "./content-blocks/agent-section";
import { getUserName } from "@/components/user-management/helpers/get-user-name";
import { GlobalErrorBoundary } from "@/error-boundary";
import { BlockErrorFallback } from "../block-error-fallback";
import {
  iOrganizationFooterConfig,
  iBrand,
} from "@/@sdk/brands/brand-model";
import { buildQuoteBrandContextValue } from "./quote-theme/build-quote-brand-context";
import { resolveBrandQuoteLegalConfig } from "@/@sdk/brands/resolve-brand-identity";
import { QuoteThemeRoot } from "./quote-theme/quote-theme-root";
import { QuotePageLayout } from "./quote-theme/quote-page-layout";

interface HolidayQuotePreviewPageProps {
  quote: iHolidayQuoteX;
  customer: iContact;
  organization: iOrganization;
  brand?: iBrand;
  agent: iUser;
  quotesDomain: string;
  footerConfig?: iOrganizationFooterConfig;
}

export default function HolidayQuoteView({
  quote,
  customer,
  organization,
  brand,
  agent,
  quotesDomain,
  footerConfig,
}: HolidayQuotePreviewPageProps) {
  const quoteAgentData = useMemo(() => {
    if (agent) {
      return {
        name: getUserName(agent),
        role: agent.data.description,
        image: agent.data.avatar,
        email: agent.credentials.email,
        phone: agent.data.phoneNumber,
        availability: {
          days: agent.data.availability_days,
          hours: agent.data.availability_hours,
        },
        bio: agent.data.bio,
      } as iAgentSectionData;
    }
    return undefined;
  }, [agent]);

  const brandPrimaryColor =
    brand?.primaryColor || organization?.data?.primaryColor;

  const brandContextValue = useMemo(
    () =>
      buildQuoteBrandContextValue({
        logo: {
          src: brand?.logo || organization?.data?.logo || "",
          alt: brand?.name || organization?.data?.name || "TA Desk",
          width: organization?.id === "exhh51" ? 220 : 120,
          height: 60,
        },
        companyName: brand?.name || organization?.data?.name || "TA Desk",
        brandPrimaryColor,
        quoteThemeConfig: brand?.quoteThemeConfig,
        quote,
        quotesDomain,
        quoteLegalConfig: brand ? resolveBrandQuoteLegalConfig(brand) : undefined,
        currentAgent: quoteAgentData,
        footerConfig,
      }),
    [
      brand,
      organization,
      brandPrimaryColor,
      quote,
      quotesDomain,
      quoteAgentData,
      footerConfig,
    ]
  );

  const { headerTemplate, quoteTheme } = brandContextValue;

  return (
    <HolidayQuoteBrandProvider value={brandContextValue}>
      <QuoteThemeRoot theme={quoteTheme}>
        <QuoteHeader quoteId={quote.id!} />

        <QuotePageLayout
          layoutConfig={quoteTheme.layoutConfig}
          headerTemplate={headerTemplate}
          quote={quote}
          greetings={
            <GlobalErrorBoundary FallbackComponent={BlockErrorFallback}>
              <QuoteGreetings
                {...quote.data}
                customerName={getContactName(customer)}
                quoteId={`QTE-${(quote.shortId || 0)
                  .toString()
                  .padStart(4, "0")}`}
                isEditable={false}
                onEdit={() => {}}
                quoteCreatedDate={quote.metadata.createdAt}
              />
            </GlobalErrorBoundary>
          }
        />

        <QuoteFooter />
      </QuoteThemeRoot>
    </HolidayQuoteBrandProvider>
  );
}
